import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ExportHubAPI, ExportProduct, ProductBomEdge } from 'api/ExportHubAPI';
import { JurisdictionPill, StatusPill, TierBadge } from './productPresets';

interface Props {
  databaseId: string;
  groupId: string | undefined;
  onOpenProduct: (_p: ExportProduct) => void;
  reloadKey?: number;
}

const MAX_DEPTH = 12;

const HierarchyView: React.FC<Props> = ({
  databaseId,
  groupId,
  onOpenProduct,
  reloadKey,
}) => {
  const [products, setProducts] = useState<ExportProduct[]>([]);
  const [edges, setEdges] = useState<ProductBomEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [res, e] = await Promise.all([
        ExportHubAPI.getProducts(databaseId, groupId, { limit: 500 }),
        ExportHubAPI.getBomEdges(databaseId, groupId),
      ]);
      setProducts(res.products);
      setEdges(e);
    } catch (err) {
      console.error('Failed to load hierarchy:', err);
    } finally {
      setLoading(false);
    }
  }, [databaseId, groupId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const byId = new Map(products.map((p) => [p.id, p]));
  const childrenOf = new Map<string, Array<{ id: string; qty: number | null }>>();
  const childIds = new Set<string>();
  edges.forEach((e) => {
    const arr = childrenOf.get(e.parent_product_id);
    const entry = { id: e.child_product_id, qty: e.quantity };
    if (arr) arr.push(entry);
    else childrenOf.set(e.parent_product_id, [entry]);
    childIds.add(e.child_product_id);
  });

  const assemblies = products.filter((p) => p.tier === 'Assembly');
  const orphans = products.filter(
    (p) => p.tier !== 'Assembly' && !childIds.has(p.id),
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderRow = (
    p: ExportProduct,
    depth: number,
    path: string,
    qty?: number | null,
  ): ReactNode[] => {
    const kids = childrenOf.get(p.id) || [];
    const isOpen = expanded.has(p.id);
    const rows: ReactNode[] = [
      <tr
        key={path}
        className='hoverable-row'
        style={{ cursor: 'pointer' }}
        onClick={() => onOpenProduct(p)}
      >
        <td>
          <span
            className='eh-tree-name'
            style={{ paddingLeft: `${depth * 22}px` }}
          >
            {kids.length > 0 ? (
              <button
                className='eh-tree-toggle'
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(p.id);
                }}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                {isOpen ? (
                  <ExpandMoreIcon style={{ fontSize: 16 }} />
                ) : (
                  <ChevronRightIcon style={{ fontSize: 16 }} />
                )}
              </button>
            ) : (
              <span className='eh-tree-spacer' />
            )}
            <span className='eh-product-name'>{p.name}</span>
            {qty ? <span className='eh-struct-qty'>× {qty}</span> : null}
            <TierBadge value={p.tier} />
          </span>
        </td>
        <td>{p.hts_code || '—'}</td>
        <td>
          <StatusPill value={p.compliance_status} />
        </td>
        <td>
          <JurisdictionPill value={p.jurisdiction} />
        </td>
        <td>{p.active_license_count}</td>
      </tr>,
    ];
    if (isOpen && depth < MAX_DEPTH) {
      kids.forEach((k) => {
        const cp = byId.get(k.id);
        if (cp) {
          rows.push(...renderRow(cp, depth + 1, `${path}/${k.id}`, k.qty));
        }
      });
    }
    return rows;
  };

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='runs-content-container'>
      <table className='runs-table'>
        <thead>
          <tr>
            <th>Product</th>
            <th>HTS Code</th>
            <th>Compliance</th>
            <th>Jurisdiction</th>
            <th>Licenses</th>
          </tr>
        </thead>
        <tbody>
          {assemblies.length === 0 && orphans.length === 0 && (
            <tr>
              <td colSpan={5} className='eh-no-match'>
                No products to show.
              </td>
            </tr>
          )}
          {assemblies.flatMap((a) => renderRow(a, 0, a.id))}
          {orphans.length > 0 && (
            <>
              <tr className='eh-tree-group'>
                <td colSpan={5}>Unparented components &amp; sub-assemblies</td>
              </tr>
              {orphans.flatMap((o) => renderRow(o, 0, `orphan-${o.id}`))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default HierarchyView;
